import React from "react";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Button from "components/CustomButtons/Button.js";
import EditIcon from "@material-ui/icons/Edit";
import EmailIcon from "@material-ui/icons/Email";
import AssignmentRoundedIcon from "@material-ui/icons/AssignmentRounded";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import LiveHelpRoundedIcon from "@material-ui/icons/LiveHelpRounded";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import { useTranslation } from "react-i18next";

/**
 * ActionButtons Component
 * Renders Edit, Assessment, and Email action buttons for a client
 * Can be used in both table and card views with responsive layouts
 *
 * @param {Object} client - Full client object with all properties
 * @param {number} index - Row/card index for tour class names
 * @param {Function} onEdit - Callback when edit button clicked
 * @param {Function} onAssessment - Callback when assessment button clicked (receives client)
 * @param {Function} onEmail - Callback when email button clicked (receives client)
 * @param {boolean} isMobile - Whether to show in mobile layout
 * @param {string} layout - 'horizontal' (table) or 'vertical' (card)
 */
export default function ActionButtons({
  client,
  index,
  onEdit,
  onAssessment,
  onEmail,
  isMobile = false,
  layout = "horizontal",
}) {
  const { t } = useTranslation();

  // Determine assessment button state
  const getAssessmentButton = () => {
    const tourClass = `tour-assessments-${index}`;

    // State 1: No assessments assigned
    if (!client.hasAssessments) {
      return (
        <Tooltip
          title={<Typography>{t("dashboard:noAssessmentsAssigned")}</Typography>}
          placement="top"
        >
          <div className={tourClass}>
            <Button
              justIcon
              color="darkBlue"
              round
              simple
              className="edit"
              disabled
            >
              <AssignmentRoundedIcon />
            </Button>
          </div>
        </Tooltip>
      );
    }

    // State 2: Has pending, not accessed LMS
    if (client.hasPending && !client.lms_accessed) {
      return (
        <Tooltip
          title={
            <React.Fragment>
              <div>
                <Typography style={{ fontSize: 14 }}>
                  {t("dashboard:noAssessmentStarted")}
                </Typography>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Typography style={{ fontSize: 14 }}>
                  {t("dashboard:clickToView")}
                </Typography>
              </div>
            </React.Fragment>
          }
          placement="top"
        >
          <div className={tourClass}>
            <Button
              justIcon
              round
              onClick={() => onAssessment(client)}
              simple
              color="danger"
              className="edit"
            >
              <WarningRoundedIcon />
            </Button>
          </div>
        </Tooltip>
      );
    }

    // State 3: Has pending, accessed LMS
    if (client.hasPending && client.lms_accessed) {
      return (
        <Tooltip
          title={
            <React.Fragment>
              <div>
                <Typography style={{ fontSize: 14 }}>
                  {t("dashboard:pendingAssessments")}
                </Typography>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Typography style={{ fontSize: 14 }}>
                  {t("dashboard:clickToView")}
                </Typography>
              </div>
            </React.Fragment>
          }
          placement="top"
        >
          <div className={tourClass}>
            <Button
              justIcon
              round
              simple
              color="warning"
              onClick={() => onAssessment(client)}
              className="edit"
            >
              <LiveHelpRoundedIcon />
            </Button>
          </div>
        </Tooltip>
      );
    }

    // State 4: No pending, accessed (all completed)
    if (!client.hasPending && client.lms_accessed) {
      return (
        <Tooltip
          title={
            <React.Fragment>
              <div>
                <Typography style={{ fontSize: 14 }}>
                  {t("dashboard:allAssessmentsCompleted")}
                </Typography>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Typography style={{ fontSize: 14 }}>
                  {t("dashboard:clickToView")}
                </Typography>
              </div>
            </React.Fragment>
          }
          placement="top"
        >
          <div className={tourClass}>
            <Button
              justIcon
              round
              simple
              color="success"
              onClick={() => onAssessment(client)}
              className="edit"
            >
              <PlaylistAddCheckIcon />
            </Button>
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const containerStyle = {
    display: "flex",
    flexDirection: layout === "vertical" ? "column" : "row",
    justifyContent: layout === "vertical" ? "center" : "center",
    alignItems: "center",
    gap: layout === "vertical" ? 8 : 0,
  };

  return (
    <div style={containerStyle}>
      {/* Edit Button */}
      <Tooltip title={t("dashboard:editDetails")} placement="top">
        <div className={`tour-edit-${index}`}>
          <Button
            justIcon
            round
            simple
            onClick={onEdit}
            color="info"
            className="like"
          >
            <EditIcon />
          </Button>
        </div>
      </Tooltip>

      {/* Assessment Button (4 states) */}
      {getAssessmentButton()}

      {/* Email Button */}
      <Tooltip
        title={
          <Typography style={{ fontSize: 14 }}>
            {t("dashboard:sendEmail")}
          </Typography>
        }
        placement="top"
      >
        <div className={`tour-mail-${index}`}>
          <Button
            justIcon
            round
            onClick={() => onEmail(client)}
            simple
            color="darkBlue"
            className="remove"
          >
            <EmailIcon />
          </Button>
        </div>
      </Tooltip>
    </div>
  );
}
